import {
  CheckIcon,
  CreditCardIcon,
  GlobeIcon,
  MapPinIcon,
} from "@phosphor-icons/react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";
import {
  useEffect,
  useMemo,
  useState,
} from "react";

import "flag-icons/css/flag-icons.min.css";

import {
  countries,
  countryContinents,
  countryNotProvided,
  COUNTRY_NOT_PROVIDED_CODE,
  findCurrency,
} from "@/client/constants/country-preferences";
import { useSaveCountryPreference } from "@/client/services/user/country-preference";
import {
  type CountryPreference,
  useCountryPreferenceStore,
} from "@/client/stores/country-preference";

type Props = {
  // Pays déduit de l'adresse IP, proposé par défaut.
  suggestedCountryCode?: string | null;
  onContinue?: (
    preference: CountryPreference,
  ) => void;
};

export const CountryPreferenceStep = ({
  suggestedCountryCode,
  onContinue,
}: Props) => {
  const reduceMotion = useReducedMotion();

  const savedPreference =
    useCountryPreferenceStore(
      (state) => state.preference,
    );

  const setPreference =
    useCountryPreferenceStore(
      (state) => state.setPreference,
    );

  const {
    saveCountryPreference,
    loading,
    error,
    reset,
  } = useSaveCountryPreference();

  const [countryCode, setCountryCode] =
    useState(() => {
      if (
        savedPreference?.countryNotProvided
      ) {
        return COUNTRY_NOT_PROVIDED_CODE;
      }

      return (
        savedPreference?.countryCode ?? ""
      );
    });

  useEffect(() => {
    if (
      countryCode ||
      !countries.some(
        (country) =>
          country.countryCode ===
          suggestedCountryCode,
      )
    ) {
      return;
    }

    setCountryCode(
      suggestedCountryCode ?? "",
    );
  }, [countryCode, suggestedCountryCode]);

  const isCountryNotProvided =
    countryCode ===
    COUNTRY_NOT_PROVIDED_CODE;

  const selectedCountry = useMemo(() => {
    if (isCountryNotProvided) {
      return countryNotProvided;
    }

    return countries.find(
      (country) =>
        country.countryCode === countryCode,
    );
  }, [
    countryCode,
    isCountryNotProvided,
  ]);

  const selectedCurrency = useMemo(() => {
    if (!selectedCountry) {
      return undefined;
    }

    return findCurrency(
      selectedCountry.currencyCode,
    );
  }, [selectedCountry]);

  const groupedCountries = useMemo(
    () =>
      countryContinents
        .map((continent) => ({
          continent,
          countries: countries.filter(
            (country) =>
              country.continent ===
              continent,
          ),
        }))
        .filter(
          (group) =>
            group.countries.length > 0,
        ),
    [],
  );

  const confirmCountry = async () => {
    if (
      !selectedCountry ||
      !selectedCurrency ||
      loading
    ) {
      return;
    }

    reset();

    try {
      /*
       * Le frontend transmet uniquement
       * le pays.
       *
       * La devise est déterminée et validée
       * par le backend afin d’éviter qu’une
       * devise arbitraire soit enregistrée.
       */
      const savedCountry =
        await saveCountryPreference({
          countryCode:
            isCountryNotProvided
              ? null
              : selectedCountry.countryCode,
        });

      /*
       * La réponse du backend constitue
       * la source de vérité pour la devise.
       */
      const serverCurrency = findCurrency(
        savedCountry.pricingCurrencyCode,
      );

      const appliedCurrency =
        serverCurrency ?? selectedCurrency;

      const preference: CountryPreference = {
        ...selectedCountry,
        countryCode:
          savedCountry.countryCode,
        currencyCode:
          savedCountry.pricingCurrencyCode,
        currencyLabel:
          appliedCurrency.label,
        countryNotProvided:
          savedCountry.countryNotProvided,
      };

      /*
       * Zustand reste un cache local
       * destiné à l’interface.
       *
       * La préférence durable est déjà
       * enregistrée en base de données.
       */
      setPreference(preference);

      onContinue?.(preference);
    } catch {
      /*
       * Le hook useSaveCountryPreference
       * transforme déjà l’erreur du serveur
       * en message affichable.
       */
    }
  };

  const canSubmit = Boolean(
    selectedCountry &&
      selectedCurrency &&
      !loading,
  );

  return (
    <motion.section
      aria-labelledby="country-dialog-title"
      aria-describedby="country-dialog-description"
      className={[
        /*
         * La carte utilise maintenant toute
         * la largeur utile du DialogContent.
         *
         * Sa bordure englobe ainsi visuellement
         * la croix native du dialogue.
         */
        "relative mx-auto w-full max-w-none",
        "overflow-hidden rounded-[24px]",
        "border border-indigo-100",
        "bg-white px-5 py-5",
        "shadow-[0_24px_70px_rgba(39,48,112,0.16)]",
        "sm:px-7 sm:py-6",
      ].join(" ")}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              y: 22,
              scale: 0.97,
            }
      }
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        y: 12,
        scale: 0.98,
      }}
      transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <div
        aria-hidden="true"
        className={[
          "pointer-events-none absolute",
          "-right-24 -top-24",
          "size-52 rounded-full",
          "bg-indigo-200/30 blur-3xl",
        ].join(" ")}
      />

      <div
        aria-hidden="true"
        className={[
          "pointer-events-none absolute",
          "-bottom-24 -left-24",
          "size-48 rounded-full",
          "bg-emerald-200/25 blur-3xl",
        ].join(" ")}
      />

      <div
        className={[
          "relative flex items-start",
          "gap-3.5 pr-10",
          "sm:pr-12",
        ].join(" ")}
      >
        <motion.span
          aria-hidden="true"
          className={[
            "grid size-11 shrink-0",
            "place-items-center rounded-2xl",
            "bg-gradient-to-br",
            "from-[#7798f8]",
            "via-[#667eea]",
            "to-[#706ce8]",
            "text-white",
            "shadow-[0_10px_24px_rgba(102,126,234,0.28)]",
          ].join(" ")}
          animate={
            reduceMotion
              ? undefined
              : {
                  y: [0, -3, 0],
                }
          }
          transition={{
            duration: 3.4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <GlobeIcon
            size={24}
            weight="duotone"
          />
        </motion.span>

        <div className="min-w-0 flex-1">
          <span
            className={[
              "block text-[10px]",
              "font-black uppercase",
              "tracking-[0.16em]",
              "text-emerald-600",
            ].join(" ")}
          >
            Bienvenue sur MonCVPro
          </span>

          <h2
            id="country-dialog-title"
            className={[
              "mt-1 text-[1.25rem]",
              "font-extrabold leading-tight",
              "tracking-[-0.025em]",
              "text-[#071451]",
              "sm:text-[1.45rem]",
            ].join(" ")}
          >
            Dans quel pays résidez-vous ?
          </h2>
        </div>
      </div>

      <p
        id="country-dialog-description"
        className={[
          "relative mt-3",
          "text-[0.76rem] font-medium",
          "leading-5 text-slate-500",
        ].join(" ")}
      >
        Tous les prix de MonCVPro seront
        affichés dans la devise de votre pays.
      </p>

      <div className="relative mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block min-w-0">
          <span
            className={[
              "mb-1.5 block",
              "text-[0.68rem] font-extrabold",
              "text-[#071451]",
            ].join(" ")}
          >
            Pays de résidence
          </span>

          <div className="relative">
            {selectedCountry ? (
              isCountryNotProvided ? (
                <span
                  aria-hidden="true"
                  className={[
                    "pointer-events-none absolute",
                    "left-3.5 top-1/2 z-10",
                    "grid size-5",
                    "-translate-y-1/2",
                    "place-items-center",
                    "text-indigo-500",
                  ].join(" ")}
                >
                  <GlobeIcon
                    size={17}
                    weight="duotone"
                  />
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className={[
                    "pointer-events-none absolute",
                    "left-3.5 top-1/2 z-10",
                    "grid size-5",
                    "-translate-y-1/2",
                    "place-items-center",
                    "overflow-hidden rounded-full",
                    "bg-white",
                    "shadow-[0_2px_8px_rgba(30,41,90,0.14)]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      `fi fi-${selectedCountry.flagCode}`,
                      "size-full rounded-full",
                      "bg-cover bg-center",
                    ].join(" ")}
                  />
                </span>
              )
            ) : (
              <span
                aria-hidden="true"
                className={[
                  "pointer-events-none absolute",
                  "left-3.5 top-1/2 z-10",
                  "-translate-y-1/2",
                  "text-slate-400",
                ].join(" ")}
              >
                <MapPinIcon
                  size={17}
                  weight="duotone"
                />
              </span>
            )}

            {/*
             * Le select reste entièrement natif.
             *
             * Il conserve donc uniquement le
             * chevron fourni par le navigateur.
             */}
            <select
              value={countryCode}
              disabled={loading}
              aria-label="Sélectionner votre pays de résidence"
              onChange={(event) => {
                reset();

                setCountryCode(
                  event.target.value,
                );
              }}
              className={[
                "h-12 w-full cursor-pointer",
                "rounded-xl border",
                "border-indigo-100",
                "bg-[#fafaff]",
                "pl-11 pr-3",
                "text-[0.7rem] font-bold",
                "text-[#071451]",
                "outline-none",
                "transition-all duration-200",
                "hover:border-indigo-300",
                "focus:border-indigo-500",
                "focus:bg-white",
                "focus:ring-4",
                "focus:ring-indigo-100/80",
                "disabled:cursor-wait",
                "disabled:opacity-60",
              ].join(" ")}
            >
              <option value="" disabled>
                Sélectionnez votre pays
              </option>

              <option
                value={
                  COUNTRY_NOT_PROVIDED_CODE
                }
              >
                MON PAYS N’EST PAS RENSEIGNÉ
              </option>

              {groupedCountries.map(
                (group) => (
                  <optgroup
                    key={group.continent}
                    label={group.continent}
                  >
                    {group.countries.map(
                      (country) => (
                        <option
                          key={
                            country.countryCode
                          }
                          value={
                            country.countryCode ??
                            ""
                          }
                        >
                          {
                            country.countryName
                          }
                        </option>
                      ),
                    )}
                  </optgroup>
                ),
              )}
            </select>
          </div>

          <span
            className={[
              "mt-1.5 block",
              "text-[0.58rem] font-medium",
              "leading-4 text-slate-400",
            ].join(" ")}
          >
            Choisissez votre pays dans la liste
            ou sélectionnez « Pays non renseigné ».
          </span>
        </label>

        <div className="block min-w-0">
          <span
            className={[
              "mb-1.5 block",
              "text-[0.68rem] font-extrabold",
              "text-[#071451]",
            ].join(" ")}
          >
            Devise appliquée
          </span>

          <div
            aria-live="polite"
            className={[
              "flex min-h-12 w-full",
              "items-center gap-2.5",
              "rounded-xl border px-3 py-2",
              "transition-all duration-200",
              selectedCountry
                ? [
                    "border-indigo-200",
                    "bg-gradient-to-br",
                    "from-indigo-50/90",
                    "to-white",
                    "shadow-[0_6px_18px_rgba(79,70,229,0.07)]",
                  ].join(" ")
                : [
                    "border-slate-200",
                    "bg-slate-50/80",
                  ].join(" "),
            ].join(" ")}
          >
            {selectedCountry ? (
              <>
                <span
                  aria-hidden="true"
                  className={[
                    "grid size-8 shrink-0",
                    "place-items-center",
                    "overflow-hidden rounded-full",
                    "bg-white",
                    "shadow-[0_3px_10px_rgba(30,41,90,0.13)]",
                  ].join(" ")}
                >
                  {isCountryNotProvided ? (
                    <GlobeIcon
                      size={18}
                      weight="duotone"
                      className="text-indigo-600"
                    />
                  ) : (
                    <span
                      className={[
                        `fi fi-${selectedCountry.flagCode}`,
                        "size-full rounded-full",
                        "bg-cover bg-center",
                      ].join(" ")}
                    />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <strong
                    className={[
                      "block truncate",
                      "text-[0.68rem]",
                      "font-extrabold",
                      "leading-4 text-[#071451]",
                    ].join(" ")}
                  >
                    {
                      selectedCountry.currencyLabel
                    }
                  </strong>

                  <small
                    className={[
                      "block truncate",
                      "text-[0.55rem]",
                      "font-semibold",
                      "leading-3.5",
                      "text-slate-500",
                    ].join(" ")}
                  >
                    {isCountryNotProvided
                      ? "Devise appliquée par défaut"
                      : `Prix affichés pour ${selectedCountry.countryName}`}
                  </small>
                </span>

                <span
                  className={[
                    "shrink-0 rounded-full",
                    "border border-indigo-200",
                    "bg-white px-2 py-1",
                    "text-[0.55rem] font-black",
                    "tracking-[0.08em]",
                    "text-indigo-700",
                  ].join(" ")}
                >
                  {
                    selectedCountry.currencyCode
                  }
                </span>
              </>
            ) : (
              <>
                <span
                  aria-hidden="true"
                  className={[
                    "grid size-8 shrink-0",
                    "place-items-center",
                    "rounded-lg bg-white",
                    "text-slate-400 shadow-sm",
                  ].join(" ")}
                >
                  <GlobeIcon
                    size={17}
                    weight="duotone"
                  />
                </span>

                <span
                  className={[
                    "text-[0.6rem] font-medium",
                    "leading-4 text-slate-400",
                  ].join(" ")}
                >
                  La devise apparaîtra après la
                  sélection du pays.
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isCountryNotProvided && (
          <motion.div
            role="note"
            className={[
              "relative mt-4 flex",
              "overflow-hidden rounded-2xl",
              "border border-indigo-200/80",
              "bg-gradient-to-br",
              "from-indigo-50 via-white",
              "to-violet-50",
              "px-3.5 py-3",
              "shadow-[0_10px_25px_rgba(49,46,129,0.09)]",
            ].join(" ")}
            initial={
              reduceMotion
                ? false
                : {
                    opacity: 0,
                    y: -8,
                    height: 0,
                  }
            }
            animate={{
              opacity: 1,
              y: 0,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              y: -6,
              height: 0,
            }}
            transition={{
              duration: 0.28,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <span
              aria-hidden="true"
              className={[
                "absolute inset-y-0 left-0",
                "w-1 rounded-full",
                "bg-gradient-to-b",
                "from-[#7798f8]",
                "via-[#667eea]",
                "to-[#4f46e5]",
              ].join(" ")}
            />

            <span
              aria-hidden="true"
              className={[
                "ml-1 grid size-9 shrink-0",
                "place-items-center rounded-xl",
                "bg-gradient-to-br",
                "from-[#7798f8]",
                "to-[#5c6fe6]",
                "text-white",
                "shadow-[0_7px_16px_rgba(79,70,229,0.25)]",
              ].join(" ")}
            >
              <CreditCardIcon
                size={19}
                weight="duotone"
              />
            </span>

            <span className="ml-3 flex min-w-0 flex-1 flex-col gap-0.5">
              <strong
                className={[
                  "text-[0.68rem] font-black",
                  "leading-4 text-[#111111]",
                ].join(" ")}
              >
                Paiement par carte bancaire
                uniquement
              </strong>

              <small
                className={[
                  "text-[0.58rem] font-semibold",
                  "leading-4 text-[#333333]",
                ].join(" ")}
              >
                Sans pays renseigné, les prix sont
                affichés en euros et le paiement
                devra être effectué exclusivement
                par carte bancaire.
              </small>
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {error && (
          <motion.div
            role="alert"
            initial={
              reduceMotion
                ? false
                : {
                    opacity: 0,
                    y: -6,
                    height: 0,
                  }
            }
            animate={{
              opacity: 1,
              y: 0,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              y: -4,
              height: 0,
            }}
            transition={{
              duration: 0.25,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            className={[
              "relative mt-4 overflow-hidden",
              "rounded-xl border",
              "border-rose-200",
              "bg-rose-50 px-3.5 py-2.5",
              "text-[0.65rem]",
              "font-semibold leading-5",
              "text-rose-700",
            ].join(" ")}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        disabled={!canSubmit}
        aria-busy={loading}
        onClick={() => {
          void confirmCountry();
        }}
        className={[
          "relative mt-5 inline-flex",
          "min-h-11 w-full",
          "items-center justify-center gap-2",
          "overflow-hidden rounded-xl",
          "bg-gradient-to-r",
          "from-[#667eea]",
          "via-[#5f6ddd]",
          "to-[#5867d2]",
          "px-5",
          "text-[0.72rem] font-extrabold",
          "text-white",
          "shadow-[0_10px_24px_rgba(79,70,229,0.26)]",
          "transition-all duration-200",
          "hover:shadow-[0_14px_30px_rgba(79,70,229,0.34)]",
          "focus-visible:outline-none",
          "focus-visible:ring-4",
          "focus-visible:ring-indigo-200",
          "disabled:cursor-not-allowed",
          "disabled:opacity-40",
          "disabled:shadow-none",
        ].join(" ")}
        whileHover={
          reduceMotion || !canSubmit
            ? undefined
            : {
                y: -2,
                scale: 1.005,
              }
        }
        whileTap={
          reduceMotion || !canSubmit
            ? undefined
            : {
                scale: 0.985,
              }
        }
      >
        {loading ? (
          <>
            <span
              aria-hidden="true"
              className={[
                "size-4 animate-spin",
                "rounded-full border-2",
                "border-white/35",
                "border-t-white",
              ].join(" ")}
            />

            Enregistrement…
          </>
        ) : (
          <>
            <CheckIcon
              size={17}
              weight="bold"
              aria-hidden="true"
            />

            Confirmer mon pays
          </>
        )}
      </motion.button>

      <p
        className={[
          "relative mt-3 text-center",
          "text-[0.56rem] font-medium",
          "leading-4 text-slate-400",
        ].join(" ")}
      >
        Vous pourrez modifier votre pays à tout
        moment dans vos paramètres.
      </p>
    </motion.section>
  );
};